/**
 * @file utils/parser/library.js
 * @copyright 2016 CBC/Radio-Canada. All rights reserved.
 */

import { isServer } from "../environmentDetection/library";
import { ENTITY_MAP } from "./constants";
import log from "../logger/library";

/**
 * This library will implement different parsing utils, e.g.
 * DOM parser
 */

/**
 * @nodeHasChildren - Checks that a node has children
 * @param {object} node - The JS object received from parsing.
 * @returns {boolean} - Returns true for a node with children; returns false for an edge node.
 */
const nodeHasChildren = (node) => Boolean(node?.children?.length);

/**
 * @findNodeName - Looks for a specific tag within a DOM node and its children
 * @param {object} node - The JS object to check for nodeName
 * @param {string} nodeName - The Tag to look for
 * @returns {array} - Returns an array of elements if found, otherwise, returns false
 */
export function findNodeName(node, nodeName, matchingNodes = []) {
    if (nodeHasChildren(node)) {
        node.children.forEach((node) => {
            if (node.name === nodeName) {
                matchingNodes.push(node);
            }
        });
    } else if (node.name === nodeName) {
        matchingNodes.push(node);
    }

    return matchingNodes.length > 0 ? matchingNodes : false;
}

/**
 * @formatCssAttribute - transforms css attribute form into javascript camelcase form
 * @param {string} attribute - inline css attribute
 * @returns {string} - Returns correctly formatted string
 */
const formatCssAttribute = (attribute) => {
    const array = attribute.split("-");
    return array
        .map((item, index) => {
            // If attribute is browser specific (-webkit , -moz)
            if (array[0] === "") {
                return item.charAt(0).toUpperCase() + item.slice(1);
            } else {
                if (index !== 0) {
                    return item.charAt(0).toUpperCase() + item.slice(1);
                }
                return item;
            }
        })
        .join("");
};

/**
 * @parseInlineCss - transforms inline css into a javascript obj that react understands
 * @param {string} cssString - inline css attributes
 * @returns {object} - Returns an object representation of the inline css styles
 */
export const parseInlineCss = (cssString) => {
    if (cssString === "") {
        return null;
    }
    const cssObj = {};
    cssString
        .trim()
        .split(";")
        .forEach((property) => {
            if (property !== "") {
                const notFoundIndex = -1;
                const propertyItem = property.split(":");
                let attribute = propertyItem[0].trim();
                const index = attribute
                    ? attribute.indexOf("-")
                    : notFoundIndex;

                // switch CSS attribute to camelCase Javascript property
                if (index !== notFoundIndex) {
                    attribute = formatCssAttribute(attribute);
                }

                let cssProp = "";

                // React strips out css properties with !important
                // this will remove !important so at least the css property goes through
                if (typeof propertyItem[1] === "string") {
                    cssProp = propertyItem[1].includes("!important")
                        ? propertyItem[1].replace("!important", "")
                        : propertyItem[1];
                }

                cssObj[attribute] = cssProp.trim();
            }
        });

    return cssObj;
};

/** Update customHtml by cleaning up the markup, requires window.document to be available
 *  @param {string} html the original html to be parsed
 *  @returns {string} sanitizedHtml the sanitized html, will remove incorrectly formed tags
 */
export const sanitizeHtml = (html) => {
    if (isServer()) {
        return;
    }

    let sanitizedHtml = "";

    if (typeof document !== "undefined") {
        const doc = document.createElement("span");
        doc.innerHTML = html;
        sanitizedHtml = doc.innerHTML;

        return sanitizedHtml;
    }

    log.error(
        "(sanitizeHtml) Tried to sanitize Html without having document available"
    );
    return sanitizedHtml;
};

const entityRegexFromMapping = new RegExp(
    Object.keys(ENTITY_MAP).join("|"),
    "g"
);

/**
 * @param {string} content - text to decode HTML entities from
 * @returns {string} decoded text
 */
export const decodeHtmlEntities = (content) =>
    content
        .replace(/&[A-Za-z0-9#]+;?/g, (match) => {
            return ENTITY_MAP[match] || match;
        })
        .replace(entityRegexFromMapping, (match) => {
            return ENTITY_MAP[match] || match;
        })
        .replace(/\s*&[A-Za-z0-9#]{2,};\s*/g, "");

export const safeDecodeHtmlEntities = (text) => {
    return typeof text === "string" ? decodeHtmlEntities(text) : "";
};
